/**
 * Act 1, beat 3 — ***Otto***.
 *
 * Spec §7: *"The fattening hall, vast and hazed. Otto shows her the slat that
 * lifts and the vent that breathes. He is too big for either. He demonstrates
 * rolling — and the empty straw pen at night is hers to roll in."*
 *
 * ── The one sentence this beat has to deliver ────────────────────────────────
 *
 * **She watches something enormous do badly what she can do beautifully.**
 *
 * Everything below serves that. Otto is not a tutorial NPC who unlocks a verb —
 * nothing here is unlocked, she could always roll. He is the first character in
 * the game who is *glad* about something, and what he is glad about is her. He
 * demonstrates rolling because he cannot really do it any more, the
 * demonstration costs him, and then he stands and looks at the empty pen across
 * the alley. That look is the whole instruction.
 *
 * ── There are no watchers in this beat, and that is content ──────────────────
 *
 * Spec §5.4 requires one consequence-free rolling space in Act 1, *before*
 * stealth pressure teaches the player never to touch the game's best-feeling
 * verb. This beat is it, so it registers **no `Watcher` at all** — not a dozing
 * one, not a distant one, not one "for atmosphere". The hall is asleep.
 *
 * That is not a saving. A beat that teaches the roll while something can see her
 * teaches the opposite of what it is for, and a later session that wants tension
 * here has to argue with spec §5.4 first.
 *
 * ── Nothing here touches the world ───────────────────────────────────────────
 *
 * Stages emit **cues**; `ottoDirector.ts` is the only file that knows Otto is
 * made of triangles. Same rule as `born.ts` and `weakestLatch.ts`, and the same
 * consequence: if a stage in this file ever imports Three, the beat has stopped
 * being replayable in Node and the mistake will be here rather than in the
 * sequencer.
 *
 * **In particular the spatial stages wait on flags rather than reading
 * `ctx.self` against the hall's own constants.** They could not do the latter
 * anyway — importing `world/fattening` pulls Three in transitively — and the
 * flag version is also the honest one: "she is deep enough into the hall" is a
 * fact about a room, and the room is what should decide it.
 */

import type { Beat, Stage, StoryContext } from '@/story/types';

/**
 * Interactable ids, shared between this beat and the world that places them.
 *
 * **Two entries, and they are the same animal.** That is not duplication; it is
 * the difference between looking at something and touching it, expressed as the
 * only thing the interaction system actually understands — a reach.
 *
 * The pen she is being shown still answers to nothing at all. See
 * `OTTO_FLAGS.rolledEnough`.
 */
export const OTTO_IDS = {
  /**
   * Otto himself, under `observe`. Holding it is `focus` (D-066), and this is
   * that camera's second customer and its first at a subject that is neither
   * walking nor man-shaped.
   */
  otto: 'otto.otto',
  /**
   * His shoulder, under `nudge`, at a reach of nine-tenths of a metre.
   *
   * **Why this is a second interactable and not a second verb on the first
   * one.** `otto.otto` reaches six metres, because `observe` is a thing she does
   * from the alley and D-071 puts a 1.05 m wall between them. Hanging `nudge` on
   * that same entry would let her pat him from across the passage through a slab
   * of concrete — the verb would work at a distance the fiction does not allow,
   * and nothing in the interaction system would object, because a reach is all it
   * knows.
   *
   * So: watching him is a thing you do at distance, touching him is a thing you
   * do beside him, and the two facts are two entries.
   */
  flank: 'otto.flank',
} as const;

/**
 * Story flags the GAME raises. Every one of them is something only the
 * simulation knows.
 */
export const OTTO_FLAGS = {
  /** She is far enough into the hall that the service door is behind her. */
  inHall: 'otto.inHall',
  /** She is close enough to his pen for him to be an animal rather than a shape. */
  atHisPen: 'otto.atHisPen',
  /**
   * She is **inside his pen**, through the broken corner of its mouth.
   *
   * The hole is 0.50 m across and 0.52 m tall, which leaves her 0.43 m of
   * headroom once she is up on the 0.09 m deck; she is 0.32 m tall. Otto is
   * 1.06 m at the shoulder. **Nothing in this beat ever says that out loud**,
   * and it does not have to — spec §3's *"cannot escape, too big"* stops being
   * an assertion the moment the player has walked through the reason.
   *
   * Not a stage gate. `in-with-him` waits on `nudged`, which she cannot raise
   * from the alley anyway; this flag exists because the room and the director
   * both want to know, and because a beat that measured only the touch would
   * have no way to tell "she never found it" from "she found it and stood
   * there".
   */
  inHisPen: 'otto.inHisPen',
  /**
   * She has touched him.
   *
   * **This is the wire that turns beat 3 from a cutscene into a scene.** The
   * demonstration used to begin because a stage before it had finished; now it
   * begins because she asked. He is the first character in the game who is glad
   * about something, and until this flag existed the player had no way to answer
   * him.
   *
   * What it costs him is the whole characterisation and it was already written:
   * `DEMO` in `ottoDirector.ts` spends longer getting him back up than putting
   * him down. She asks, he answers, and answering hurts.
   */
  nudged: 'otto.nudged',
  /**
   * She has rolled twelve cumulative metres **inside the straw pen**.
   *
   * Distance, not time and not a count of rolls, because the beat is about the
   * *feel* of the verb and twelve metres is three or four good runs — long
   * enough to have met the fatigue curve and the cooldown, short enough not to
   * become a chore. Counting rolls would reward tapping the key; counting
   * seconds would reward holding it against a wall.
   *
   * **There is no prompt and no interactable.** She is not asked to do this and
   * nothing tells her it is being measured. The pen is simply somewhere she can,
   * and the stage ends when she has.
   */
  rolledEnough: 'otto.rolledEnough',
  /**
   * She is standing in the straw pen.
   *
   * Emitted so the beat can offer the pen at the right moment; the roll counter
   * itself is gated on the same fact in `main.ts`.
   */
  inStrawPen: 'otto.inStrawPen',
  /**
   * She is at the vent in the far wall.
   *
   * **This is the beat's ending, and it is the first ending in Act 1 that is an
   * act rather than a clock.** Beats 1 and 3 both finished on a hold: the screen
   * faded while she stood where she already was, and the next beat opened
   * somewhere else. Played in order that reads as the game moving her, which is
   * the opposite of what every one of these beats is about — Alex, on playing the
   * act: *"there is not really anything to do to progress and then Rosalie is
   * suddenly in the next room."*
   *
   * The vent has been in that wall since session 17, low enough to be obviously
   * pig-sized and obviously not Otto-sized, and he has been looking at it. It
   * costs nothing to use and it makes the cut to the service corridor a
   * consequence of something she did.
   */
  atVent: 'otto.atVent',
} as const;

/** Every cue this beat can emit. The director switches on these and nothing else. */
export const OTTO_CUES = {
  /** Night in the fattening hall. Three of five high-bays, and she is at the door. */
  open: 'otto.open',
  /** He is close enough to resolve. He notices her, and he is pleased. */
  noticesHer: 'otto.noticesHer',
  /** `observe` comes alive on him. */
  offerOtto: 'otto.offerOtto',
  /**
   * He turns his head to the broken corner of his own pen mouth.
   *
   * **That is the invitation, and it is the only one given.** No prompt, no
   * highlight, no text — D-087 settled that this beat says what it has to say
   * with a light and a look, after a version that said nothing at all was played
   * and reported as having no goal in it. He is the one thing in this building
   * that can point at something.
   *
   * The director is allowed to break the look when she is already inside: an
   * animal staring at a hole while the visitor stands at his cheek is a prop
   * running a script, and he is not one.
   */
  showsTheGap: 'otto.showsTheGap',
  /**
   * `nudge` comes alive on his shoulder — `OTTO_IDS.flank`, at 0.9 m.
   *
   * Offered with the invitation rather than from boot, so the prompt cannot
   * appear before there is a way to reach him. It is never taken away again;
   * spec §10 and every patience clock in this file work the same way.
   */
  offerFlank: 'otto.offerFlank',
  /**
   * He does it. The director owns the nine seconds that follow this cue, because
   * the shape of a heavy animal getting onto its back and off again is
   * presentation and the beat has no business timing it in fixed steps.
   */
  demonstrate: 'otto.demonstrate',
  /** He is back on his feet, and he turns his head to the pen across the alley. */
  looksAcross: 'otto.looksAcross',
  /** The straw is hers. No prompt — this is a light and a look, not a UI. */
  hersNow: 'otto.hersNow',
  /** She has had the pen. He settles. */
  enough: 'otto.enough',
  /** Fade to black. */
  fadeOut: 'otto.fadeOut',
  /** The beat is over. */
  end: 'otto.end',
} as const;

/**
 * How long the beat waits on a player who has not worked out a verb.
 *
 * Same number and same reasoning as beats 1 and 2: spec §10 forbids softlocks,
 * and "never worked out that `observe` does something" is a softlock with better
 * manners. When a stage times out the verb is **not** taken away.
 */
const PATIENCE = 45;

/**
 * How long the beat waits on a player who has not gone in with him.
 *
 * **And what happens when it expires is the reason this revision could be made
 * at all.** The stage falls through and he demonstrates anyway, from the alley,
 * behind the wall — which is D-078's shot, unchanged, down to the hooves coming
 * over the rail because they are the only part of him that clears 1.05 m.
 *
 * So this beat now has one animation and two framings, and which one the player
 * gets depends on whether they went in. Nothing branches, nothing is cut, and
 * the version that shipped in session 17 is still in the game as the version you
 * get for ignoring the hole. A revision that deleted D-078's framing would have
 * had to be right about a judgement made with the room in front of it; this one
 * only has to be right that being closer is better.
 *
 * Sixty rather than `PATIENCE`'s forty-five: finding a 0.5 m hole in the dark at
 * the far corner of a pen mouth is a slower discovery than working out that a
 * key does something, and unlike every other clock in this file, the thing on
 * the far side of it is worth waiting for.
 *
 * **One clock and not two.** An earlier shape of this stage waited first for her
 * to be in the pen and then for her to touch him, which spent 105 s doing
 * nothing to a player who never found the way in — two patience clocks in series
 * are a softlock that has learned to count.
 */
const GAP_PATIENCE = 60;

/**
 * How long the beat waits on a player who has not rolled in the straw.
 *
 * Twice `PATIENCE`, and the asymmetry is the point. Every other patience clock
 * in the game covers a player who has not *discovered* something; this one
 * covers a player who has discovered it and would rather look at the room. Both
 * are fine, and the second deserves longer, because the pen is the one place in
 * Act 1 where doing nothing costs nothing.
 */
const ROLL_PATIENCE = 90;

/**
 * How long the beat waits on a player who has not walked to the vent.
 *
 * `ROLL_PATIENCE`'s number, for `ROLL_PATIENCE`'s reason: this is not covering a
 * player who has failed to *discover* something — the vent is a hole in a wall
 * that Otto has been looking at — it is covering a player who is still enjoying
 * the one room in Act 1 that costs nothing. Both deserve the longer clock.
 *
 * It is also the number that decides how long beat 3 runs untouched, which is
 * what `otto.spec.ts`'s softlock test sits through. See `OTTO_FLAGS.atVent`.
 */
const LEAVE_PATIENCE = 90;

/** A stage that is purely a length of time with cues at the front of it. */
function hold(id: string, seconds: number, ...cues: readonly string[]): Stage {
  return {
    id,
    enter: (_ctx, emit): void => {
      for (const cue of cues) emit(cue);
    },
    done: (ctx: StoryContext): boolean => ctx.stageTime >= seconds,
  };
}

/** A stage that waits for a flag, or for the player to have had long enough. */
function waitFor(id: string, flag: string, patience: number, ...cues: readonly string[]): Stage {
  return {
    id,
    enter: (_ctx, emit): void => {
      for (const cue of cues) emit(cue);
    },
    done: (ctx: StoryContext): boolean => ctx.completed.has(flag) || ctx.stageTime >= patience,
  };
}

/** A stage that waits for a flag and will not give up, because the flag is the goal. */
function waitUntil(id: string, flag: string, ...cues: readonly string[]): Stage {
  return {
    id,
    enter: (_ctx, emit): void => {
      for (const cue of cues) emit(cue);
    },
    done: (ctx: StoryContext): boolean => ctx.completed.has(flag),
  };
}

export const OTTO_BEAT: Beat = {
  number: 3,
  id: 'act1.otto',
  stages: [
    // ── arrive ───────────────────────────────────────────────────────────────
    // No camera hold. Beat 1 earned the right to open in gameplay and beat 2
    // kept it; a third beat opening on a held shot would be the game taking back
    // something it has twice given.
    //
    // `waitUntil`, not `waitFor`: there is no patience clock here because there
    // is nothing to discover. She walks forward. If she does not, the hall is
    // twenty-four metres of night and she is welcome to look at it.
    waitUntil('arrive', OTTO_FLAGS.inHall, OTTO_CUES.open),

    // ── find him ─────────────────────────────────────────────────────────────
    // He is eleven metres away through the haze and is a shape long before he is
    // an animal. Also without patience, and for the same reason: walking up an
    // alley is not a puzzle.
    waitUntil('find-him', OTTO_FLAGS.atHisPen),

    // ── watch him ────────────────────────────────────────────────────────────
    // `observe` is offered on him, and holding it is `focus`. A player who never
    // holds it gets the stage anyway after PATIENCE — and loses nothing, because
    // what `focus` buys here is a better-composed shot of something that is
    // about to happen in front of them either way.
    //
    // `noticesHer` fires with the offer rather than before it. He is the first
    // character in the game who is glad about something and the player should be
    // looking at him when it happens.
    waitFor('watch-him', OTTO_IDS.otto, PATIENCE, OTTO_CUES.noticesHer, OTTO_CUES.offerOtto),

    // ── in with him ──────────────────────────────────────────────────────────
    // **The stage this revision exists for.** Alex, playing the act: *"beat 3 is
    // a bit too distant when it comes to recognize otto."*
    //
    // He looks at the broken corner of his own pen mouth; she goes through it;
    // she touches him. The bottom of that panel has been smashed out at the kerb
    // — 0.50 m across and 0.52 m tall, which she walks through and he could not
    // get his head into. **D-071 does not move for this.** The wall is still
    // 1.05 m, she still cannot see over it, and what she sees coming up the
    // alley is still his back clearing an unbroken concrete line. The hole is at
    // the far corner, it is low, and it is not what she finds first.
    //
    // Waiting on `nudged` rather than on `inHisPen` is what makes this one stage
    // instead of two — see `GAP_PATIENCE`, which also says what happens to the
    // player who never goes in, and it is not a punishment.
    waitFor('in-with-him', OTTO_FLAGS.nudged, GAP_PATIENCE, OTTO_CUES.showsTheGap, OTTO_CUES.offerFlank),

    // ── the demonstration ────────────────────────────────────────────────────
    // Shoulder down, a heavy log-roll onto his back and back down, and a thump
    // into the pen wall that is the loudest thing in the beat. Then he stands.
    //
    // Nine seconds, held rather than waited on: **she asked for this and she
    // cannot now hurry it**, which is the difference between watching an animal
    // and operating one. `in-with-him` is the whole of the player's say in it.
    // The director owns every beat inside the nine — see `OTTO_CUES.demonstrate`.
    //
    // The stage does not know or care where she is standing, and that is what
    // keeps the revision honest: one animation, and the framing is decided by
    // whether she went in. See `GAP_PATIENCE`.
    {
      ...hold('the-demonstration', 9, OTTO_CUES.demonstrate),
      exit: (_ctx, emit): void => emit(OTTO_CUES.looksAcross),
    },

    // ── the look ─────────────────────────────────────────────────────────────
    // He holds it. Three seconds of an enormous animal looking at an empty pen,
    // which is the entire instruction and the only one this beat gives.
    hold('the-look', 3),

    // ── hers ─────────────────────────────────────────────────────────────────
    // No watchers, no suspicion, no consequence, and **no prompt** — see
    // `OTTO_FLAGS.rolledEnough`. `ROLL_PATIENCE` rather than none, because unlike
    // beat 2's window this stage is not the only way out of a room: she is
    // already where the beat wanted her, and a player who would rather stand in
    // the straw and look at Otto has understood it.
    waitFor('hers', OTTO_FLAGS.rolledEnough, ROLL_PATIENCE, OTTO_CUES.hersNow),

    // ── the way out ──────────────────────────────────────────────────────────
    // She has had the pen, and Otto settles. **That is the only instruction this
    // stage gives**, and it is the same kind of instruction as the look across
    // the alley two stages ago: an enormous animal changing what it is doing, and
    // the player working out what it means.
    //
    // `enough` moved here from `close`, where it fired underneath the fade and
    // nobody ever saw it.
    //
    // `LEAVE_PATIENCE` and not `waitUntil`, and the asymmetry with beat 2's
    // window is deliberate. There, getting out was the beat and a timeout would
    // have meant the beat not happening. Here the beat has already happened —
    // she has been shown the roll and given the straw — and what remains is a
    // walk to a hole in a wall she has been able to see since she came in. Two
    // minutes is long enough that anybody looking for it will find it, and spec
    // §10 does not permit the alternative.
    waitFor('the-way-out', OTTO_FLAGS.atVent, LEAVE_PATIENCE, OTTO_CUES.enough),

    // ── out ──────────────────────────────────────────────────────────────────
    // The chute — spec §5.4's Act 1 set-piece, the roll run out of the hall — is
    // `docs/beat-3-plan.md`'s declared cut line and is not here. It is a *second*
    // showcase of a verb this beat has already showcased once, in a pen, with
    // Otto watching, and it now defers into a room that already exists, which is
    // the cheapest kind of deferral there is. Filed against beat 4.
    {
      ...hold('close', 4.5, OTTO_CUES.fadeOut),
      exit: (_ctx, emit): void => emit(OTTO_CUES.end),
    },
  ],
};
